import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useInfiniteList } from '@/hooks/useInfiniteList';

import { profesionalesApi } from '../api';
import type {
  Profesional,
  ProfesionalCreate,
  ProfesionalListParams,
  ProfesionalUpdate,
} from '../types';

const QUERY_KEY_BASE = ['profesionales'] as const;

export const profesionalesKeys = {
  all: QUERY_KEY_BASE,
  list: (params: ProfesionalListParams) => [...QUERY_KEY_BASE, 'list', params] as const,
  detail: (uid: string) => [...QUERY_KEY_BASE, uid] as const,
};

export function useProfesionales(params: ProfesionalListParams = {}) {
  return useInfiniteList({
    queryKey: profesionalesKeys.list(params),
    queryFn: ({ signal, ...rest }) => profesionalesApi.list({ ...rest, signal }),
    params,
  });
}

export function useProfesional(uid: string | undefined) {
  return useQuery({
    queryKey: uid ? profesionalesKeys.detail(uid) : profesionalesKeys.all,
    queryFn: ({ signal }) => profesionalesApi.getById(uid!, signal),
    enabled: !!uid,
  });
}

export function useCreateProfesional() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ProfesionalCreate) => profesionalesApi.create(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: profesionalesKeys.all });
    },
  });
}

export function useUpdateProfesional() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uid, data }: { uid: string; data: ProfesionalUpdate }) =>
      profesionalesApi.update(uid, data),
    onSuccess: (updated: Profesional) => {
      void queryClient.invalidateQueries({ queryKey: profesionalesKeys.all });
      queryClient.setQueryData(profesionalesKeys.detail(updated.uid_profesional), updated);
    },
  });
}

export function useDeleteProfesional() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (uid: string) => profesionalesApi.remove(uid),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: profesionalesKeys.all });
    },
  });
}
